import { cloneDeep } from 'lodash'
import arrayMove from 'array-move'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AiTool, AiStepSchema, DialogId } from '@feedyou-ai/feedbot-schema'
import { Mutable, PropertyValuePair, SelectedMutations } from '@feedyou/utils'

import { isDialogTypeTool } from './utils'
import { generateId } from '../../../lib/generateId'
import { updateMutableValue } from '../../Step/StepService'

type AiStepReducerState = Mutable<AiStepSchema>
type WithMutations<T extends object> = T & { selectedMutations: SelectedMutations | null }

const aiSlice = createSlice({
  name: 'ai',
  initialState: {} as AiStepReducerState,
  reducers: {
    setGoal: (step, action: PayloadAction<string>) => {
      step.goal = action.payload
    },
    setInstructions: (step, action: PayloadAction<string>) => {
      step.instructions = action.payload
    },
    setDialogForDialogTool: (
      step,
      action: PayloadAction<{ toolId: AiTool['id']; dialogId?: DialogId }>
    ) => {
      const { toolId, dialogId } = action.payload
      if (step?.tools?.length) {
        const toolIndex = step.tools.findIndex(({ id }) => id === toolId)
        if (toolIndex >= 0 && isDialogTypeTool(step.tools[toolIndex])) {
          step.tools[toolIndex].action = { ...step.tools[toolIndex].action, dialog: dialogId }
        }
      }
    },

    editToolProperty: (
      step,
      action: PayloadAction<WithMutations<PropertyValuePair<AiTool> & { toolId: string }>>
    ) => {
      const { toolId, property, value, selectedMutations } = action.payload
      if (step?.tools?.length) {
        const toolIndex = step.tools.findIndex(({ id }) => id === toolId)
        if (toolIndex >= 0) {
          const tool = cloneDeep<any>(step.tools[toolIndex])
          step.tools[toolIndex] = updateMutableValue(tool, property, value, selectedMutations)
        }
      }
    },
    moveTool: (step, action: PayloadAction<{ sourceIndex: number; destinationIndex: number }>) => {
      const { sourceIndex: srcIndex, destinationIndex } = action.payload
      const destIndex = srcIndex >= destinationIndex ? destinationIndex : destinationIndex - 1
      step.tools = arrayMove(step.tools || [], srcIndex, destIndex)
    },
    deleteTool: (step, action: PayloadAction<{ index: number }>) => {
      step.tools = step.tools?.filter((_, index) => index !== action.payload.index)
    }
  }
})

export const actions = aiSlice.actions
export const reducer = aiSlice.reducer
